// Relative "time since" labels driven by one shared clock.
//
// Every mounted consumer subscribes to a single module-level ticker instead of
// owning its own interval. The ticker runs only while something is subscribed,
// pauses while the tab is hidden, and consumers re-render only when their
// label string actually changes (setState bails out on an equal string), so a
// long feed costs one timer and a few string compares per second.

import React from "react";
import { parseUtcTimestampToMs, TimestampInput } from "./timezone";

const TICK_MS = 1000;

let nowMs = Date.now();
let timerId: number | undefined;
const listeners = new Set<() => void>();

const emit = () => {
  nowMs = Date.now();
  listeners.forEach((listener) => listener());
};

const startTicker = () => {
  if (timerId !== undefined || document.hidden) return;
  emit();
  timerId = window.setInterval(emit, TICK_MS);
};

const stopTicker = () => {
  if (timerId === undefined) return;
  window.clearInterval(timerId);
  timerId = undefined;
};

const handleVisibilityChange = () => {
  if (document.hidden) stopTicker();
  else startTicker();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    startTicker();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopTicker();
    }
  };
};

// While the ticker is idle (no subscribers yet, or tab hidden) the cached clock
// is stale, so read the real one; otherwise use the tick value for stable
// snapshots.
const readNow = () => (timerId === undefined ? (nowMs = Date.now()) : nowMs);

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "Just now" under 10s, then 10s steps, then whole minutes, hours, days. */
export const formatTimeSince = (timestampMs: number, now: number) => {
  if (!Number.isFinite(timestampMs)) return "-";

  const elapsed = Math.max(0, now - timestampMs);
  if (elapsed < 10 * SECOND_MS) return "Just now";
  if (elapsed < MINUTE_MS) {
    return `${Math.floor(elapsed / (10 * SECOND_MS)) * 10}s ago`;
  }
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m ago`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h ago`;
  return `${Math.floor(elapsed / DAY_MS)}d ago`;
};

export const useTimeSince = (timestamp: TimestampInput) => {
  const timestampMs = React.useMemo(
    () => parseUtcTimestampToMs(timestamp),
    [timestamp]
  );
  const [state, setState] = React.useState(() => ({
    timestampMs,
    label: formatTimeSince(timestampMs, readNow()),
  }));

  React.useEffect(() => {
    const update = () => {
      const label = formatTimeSince(timestampMs, nowMs);
      setState((prev) =>
        prev.timestampMs === timestampMs && prev.label === label
          ? prev
          : { timestampMs, label }
      );
    };
    // Subscribing starts the ticker (refreshing nowMs) if it was idle, so
    // sync once afterwards; equal strings bail out without re-rendering.
    const unsubscribe = subscribe(update);
    update();
    return unsubscribe;
  }, [timestampMs]);

  // If the timestamp changed, never show the previous item's label, not even
  // for the one frame before the effect resyncs.
  return state.timestampMs === timestampMs
    ? state.label
    : formatTimeSince(timestampMs, readNow());
};
